import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { pricingPlans } from '../data/mock';
import { Check, Star, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { Badge } from './ui/badge';

const PricingCard = ({ plan, isCenter, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={cardRef}
      className={`relative flex flex-col h-full ${isCenter ? 'lg:-translate-y-8 lg:scale-[1.03]' : ''}`}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.15, ease: "easeOut" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.03, transition: { duration: 0.3 } }}
    >
      {/* Popular Badge */}
      {plan.isPopular && (
        <motion.div 
          className="absolute -top-4 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0, y: -10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Badge className="bg-gradient-to-r from-violet-600 via-purple-600 to-blue-600 text-white px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 rounded-full border-none shadow-[0_0_30px_rgba(139,92,246,0.6)]">
            <Star size={14} fill="currentColor" />
            RECOMMENDED
          </Badge>
        </motion.div>
      )}

      {/* iOS 16 Glass Card */}
      <div
        className={`relative flex flex-col h-full p-8 rounded-3xl transition-all duration-500 overflow-hidden ios-card ${
          plan.isPopular ? 'border-violet-500/30' : ''
        } ${isHovered ? 'shadow-[0_20px_60px_rgba(139,92,246,0.25)]' : ''}`}
      >
        {/* Glow effect for popular card */}
        {plan.isPopular && (
          <>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-violet-500 to-transparent" />
            <motion.div 
              className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-40 bg-gradient-to-b from-violet-500/30 to-transparent blur-[80px] -translate-y-1/2"
              animate={{ opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </>
        )}

        {/* Hover gradient overlay */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-transparent to-blue-500/10 rounded-3xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />

        {/* Plan Header */}
        <div className="mb-6 relative z-10">
          <p className="text-violet-400 text-sm font-semibold tracking-wider mb-2">
            {plan.name}
          </p>
          <h3 className="text-2xl font-bold text-white mb-1">{plan.tier}</h3>
          <p className="text-white/50 text-sm">{plan.idealFor}</p>
        </div>

        {/* Price */}
        <div className="mb-6 relative z-10">
          <div className="flex items-baseline gap-1">
            <span className="text-lg text-white/60">$</span>
            <span className="text-5xl font-bold text-gradient-violet">{plan.price}</span>
            <span className="text-white/40 text-lg ml-1">one-time</span>
          </div>
        </div>

        {/* Delivery Time */}
        <div className="flex items-center gap-2 mb-6 pb-6 border-b border-white/10 relative z-10">
          <Clock size={16} className="text-blue-400" />
          <span className="text-white/70 text-sm">{plan.delivery}</span>
        </div>

        {/* Includes From */}
        {plan.includesFrom && (
          <p className="text-white/50 text-sm mb-4 relative z-10">
            Includes everything in <span className="text-violet-400 font-medium">{plan.includesFrom}</span>, plus:
          </p>
        )}

        {/* Features List */}
        <ul className="space-y-4 mb-8 flex-grow relative z-10">
          {plan.features.map((feature, idx) => (
            <motion.li 
              key={idx} 
              className="flex items-start gap-3 group"
              initial={{ opacity: 0, x: -10 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.3 + idx * 0.05 }}
            >
              <div className="mt-1 flex-shrink-0 w-5 h-5 bg-gradient-to-br from-violet-500/30 to-blue-500/20 rounded-md flex items-center justify-center group-hover:from-violet-500/50 group-hover:to-blue-500/40 transition-colors">
                <Check size={12} className="text-violet-400" />
              </div>
              <span className="text-white/70 text-sm leading-relaxed group-hover:text-white transition-colors">{feature}</span>
            </motion.li>
          ))}
        </ul>

        {/* Note */}
        {plan.note && (
          <p className="text-white/40 text-xs italic mb-6 border-t border-white/10 pt-4 relative z-10">
            *{plan.note}
          </p>
        )}

        {/* CTA Button */}
        <button
          onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
          className={`group w-full py-4 text-lg font-medium flex items-center justify-center gap-2 transition-all duration-400 relative overflow-hidden z-10 rounded-xl ${
            plan.isPopular
              ? 'bg-gradient-to-r from-violet-600 via-purple-600 to-blue-600 text-white hover:shadow-[0_0_40px_rgba(139,92,246,0.5)]'
              : 'glass text-white hover:bg-gradient-to-r hover:from-violet-600/80 hover:to-blue-600/80'
          }`}
        >
          <span className="relative z-10">Select Plan</span>
          <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </div>
    </motion.div>
  );
};

const PricingSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <section ref={sectionRef} id="pricing" className="py-32 lg:py-40 relative overflow-hidden">
      {/* Parallax Background Elements */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ y }}
      >
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-violet-600/20 via-purple-600/10 to-transparent blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-tl from-blue-600/20 via-indigo-600/10 to-transparent blur-[120px]" />
      </motion.div>

      {/* Floating shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div 
          className="absolute top-20 right-[10%] w-16 h-16 border border-violet-500/20 rounded-2xl rotate-12 animate-float-slow"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, -100]) }}
        />
        <motion.div 
          className="absolute bottom-40 left-[5%] w-12 h-12 bg-gradient-to-br from-blue-500/20 to-violet-500/10 rounded-full animate-float"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, 100]) }}
        />
      </div>

      <motion.div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 glass px-5 py-2.5 mb-6 rounded-full"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Sparkles size={16} className="text-violet-400" />
            <span className="text-violet-400 text-sm font-semibold tracking-wider">TRANSPARENT PRICING</span>
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Choose Your <span className="text-gradient-mixed">Package</span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            No hidden fees. No surprises. Just quality web design tailored to your needs.
          </p>
        </motion.div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {pricingPlans.map((plan, index) => (
            <PricingCard key={plan.id} plan={plan} isCenter={index === 1} index={index} />
          ))}
        </div>

        {/* Bottom Note */}
        <motion.p 
          className="text-center text-white/40 text-sm mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          All packages include free consultation. Custom solutions available upon request.
        </motion.p>
      </motion.div>
    </section>
  );
};

export default PricingSection;
